import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LogOut, Menu, X } from 'lucide-react';
import { shouldShowLogoutIcon } from '../data/site_settings';

export default function CenteredNavbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const handleLogout = () => {
    localStorage.removeItem('mrg-authenticated');
    localStorage.removeItem('mrg-last-active');
    window.location.reload();
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 zr-header ${isScrolled ? 'bg-brand-bg shadow-md' : 'bg-brand-bg'}`} style={{ height: '64px', minHeight: '64px' }}>
      <div className="container mx-auto px-3 md:px-8" style={{ height: '100%' }}>
        <div className="flex items-center justify-between" style={{ height: '100%' }}>
          {/* Left Menu Items (Desktop) */}
          <div className="hidden md:flex items-center space-x-8">
            <LeftNavLinks location={location} />
          </div>

          {/* Centered Logo */}
          <div className="absolute left-0 right-0 mx-auto w-full flex justify-center items-center pointer-events-none">
            <Link to="/" className="pointer-events-auto">
              <img
                src="/assets/image.png_7846.svg"
                alt="MRG Logo"
                className="h-8 w-auto"
              />
            </Link>
          </div>

          {/* Right Menu Items (Desktop) */}
          <div className="hidden md:flex items-center space-x-8">
            <RightNavLinks location={location} />
            {shouldShowLogoutIcon() && (
              <button
                onClick={handleLogout}
                className="flex items-center space-x-1 text-gray-300 hover:text-brand-text transition-colors duration-300"
                aria-label="Log out"
                title="Return to password screen"
              >
                <LogOut size={16} />
              </button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden focus:outline-none" 
            onClick={toggleMenu}
            aria-label={isMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={isMenuOpen}
            title={isMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {isMenuOpen ? (
              <X size={24} className="text-brand-text" />
            ) : (
              <Menu size={24} className="text-brand-text" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-brand-bg absolute top-full left-0 right-0 shadow-md">
          <div className="container mx-auto px-6 py-4 flex flex-col space-y-4">
            <div className="border-b border-gray-700 pb-4">
              <LeftNavLinks location={location} mobile />
            </div>
            <div>
              <RightNavLinks location={location} mobile />
            </div>
            {shouldShowLogoutIcon() && (
              <button
                onClick={handleLogout}
                className="flex items-center justify-center w-full mt-4 px-4 py-2 border border-brand-text text-brand-text hover:bg-brand-text hover:text-brand-bg transition-colors duration-300"
              >
                <LogOut size={16} className="mr-2" />
                <span>Log Out</span>
              </button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}

// Left side navigation links (Properties, Services, About)
function LeftNavLinks({ location, mobile = false }: { location: { pathname: string }; mobile?: boolean }) {
  const [servicesOpen, setServicesOpen] = useState(false);

  const navItems = [
    { path: '/properties', label: 'PROPERTIES' },
    {
      type: 'dropdown', label: 'SERVICES', items: [
        { path: '/services/landlord-representation', label: 'Landlord Representation' },
        { path: '/services/tenant-representation', label: 'Tenant Representation' },
        { path: '/services/investment-sales', label: 'Investment Sales' },
      ]
    },
    { path: '/about', label: 'ABOUT' },
  ];

  return (
    <>
      {navItems.map((item) => (
        item.type === 'dropdown' ? (
          <div
            key={item.label}
            className="relative"
            onMouseEnter={() => !mobile && setServicesOpen(true)}
            onMouseLeave={() => {
              if (!mobile) {
                // Add a small delay before closing the dropdown
                setTimeout(() => {
                  setServicesOpen(false);
                }, 300);
              }
            }}
          >
            <button
              className={`text-sm font-medium tracking-wider transition-colors duration-300 ${mobile ? 'block py-2 text-left w-full' : 'px-2 py-1'} ${location.pathname.includes(item.label.toLowerCase())
                  ? 'text-brand-text font-semibold'
                  : mobile ? 'text-gray-300 hover:text-brand-text' : 'text-gray-300 hover:text-brand-text'
                }`}
              onClick={() => mobile && setServicesOpen(!servicesOpen)}
              aria-haspopup="true"
              aria-expanded={servicesOpen}
            >
              {item.label}
            </button>

            {(servicesOpen || mobile) && (
              <div
                className={`${mobile ? 'static mt-2 pl-4 space-y-2' : 'absolute left-0   mt-0 w-56 z-50 bg-brand-bg shadow-lg border border-gray-700'}`}
                onMouseEnter={() => !mobile && setServicesOpen(true)}
                onMouseLeave={() => !mobile && setServicesOpen(false)}
              >
                {item.items.map((subItem) => (
                  <Link
                    key={subItem.label}
                    to={subItem.path}
                    className={`${mobile ? 'block py-1' : 'block px-4 py-3 hover:bg-gray-800'} text-base text-gray-300 hover:text-brand-text transition-colors`}
                    onClick={() => setServicesOpen(false)}
                    onMouseEnter={() => !mobile && setServicesOpen(true)}
                  >
                    {subItem.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
        ) : (
          <Link
            key={item.path!}
            to={item.path!}
            className={`text-sm font-medium tracking-wider transition-colors duration-300 ${mobile ? 'block py-2' : ''} ${location.pathname === item.path
                ? 'text-brand-text font-semibold'
                : mobile ? 'text-gray-300 hover:text-brand-text' : 'text-gray-300 hover:text-brand-text'
              }`}
          >
            {item.label}
          </Link>
        )
      ))}
    </>
  );
}

// Right side navigation links (Team, Careers, Contact)
// Articles now live in the footer (see components/Footer.tsx)
function RightNavLinks({ location, mobile = false }: { location: { pathname: string }; mobile?: boolean }) {
  const navItems = [
    { path: '/team', label: 'TEAM' },
    { path: '/careers', label: 'CAREERS' },
    { path: '/contact', label: 'CONTACT' },
  ];

  return (
    <>
      {navItems.map((item) => (
        <Link
          key={item.path}
          to={item.path}
          className={`text-sm font-medium tracking-wider transition-colors duration-300 ${mobile ? 'block py-2' : ''} ${location.pathname === item.path
              ? 'text-brand-text font-semibold'
              : mobile ? 'text-gray-300 hover:text-brand-text' : 'text-gray-300 hover:text-brand-text'
            }`}
        >
          {item.label}
        </Link>
      ))}
    </>
  );
}
